/* eslint-disable camelcase */
import { Button, Card, CardContent, Grid, Stack } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import useTitle from 'utils/customHook/useTitle'
import { getStationTitleWithTownName } from 'utils/StationUtils'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import Checkbox from 'components/forms/Checkbox'
import { useEffect } from 'react'
import AlertAction from 'alerting/actions/AlertAction'
import ParameterAction from 'referencial/components/parameter/actions/ParameterAction'
import { nbPerPageLabelHuge, SANDRE } from 'referencial/constants/ReferencialConstants'
import { getSandreLabel } from 'utils/StringUtil'
import { exportFile } from 'utils/ExportDataUtil'
import DtoPollutionSourceActivityParams from '../dto/DtoPollutionSourceActivityParams'
import ProgressCard from 'components/card/ProgressCard'
import SelectionSelect from 'components/forms/specific/SelectionSelect'
import MultiAutocomplete from 'components/forms/MultiAutocomplete'
import { intersection, sortBy } from 'lodash'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { SELECTION } from 'quality/constants/QualityConstants'
import { CardTable } from 'components/datatable/NewTable'
import { darkBlue } from 'utils/constants/ColorTheme'

const exportHeaders = ['parameterCode', 'parameterName', 'priority', 'identifier', 'installationName', 'insee', 'city', 'x', 'y', 'projection', 'category', 'activityCode', 'activityLibelle', 'inActivity', 'icpe', 'dischargeAgreement']

const STEPPollutionSearchApp = () => {
    const dispatch = useDispatch()
    const {
        installation,
        pollutionSourceParametersFullData,
        sandreCodes,
        parameters,
        citiesIndex,
    } = useSelector(store => ({
        installation: store.InstallationReducer.installation,
        pollutionSourceParametersFullData: store.AlertReducer.pollutionSourceParametersFullData,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        parameters: store.ParameterReducer.parameters,
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const townName = useMemo(() => citiesIndex[installation.townCode]?.name, [citiesIndex, installation])

    useTitle(() => [{
        title: i18n.installation,
        href: 'installation',
    }, {
        title: getStationTitleWithTownName(installation, townName),
        href: `station/installation/${installation.id}`,
    }, {
        title: i18n.pollutionSearch,
        href: `station/installation/${installation.id}/STEPPollutionSearch`,
    }], [townName, installation])

    const [dataloaded, setDataLoaded] = useState(false)
    const [inActivity, setInActivity] = useState(true)
    const [dischargeAgreement, setDischargeAgreement] = useState()
    const [icpe, setIcpe] = useState()
    const [startDate, setStartDate] = useState()
    const [endDate, setEndDate] = useState()
    const [selectionName, setSelectionName] = useState(useLocalStorage(`SELECTION_FILTERS_${SELECTION.PC}`), [])
    const [selectionCodes, setSelectionCodes] = useState()
    const [parametersSelected, setParametersSelected] = useState([])

    useEffect(() => {
        dispatch(ParameterAction.fetchParameters())

        if (selectionName?.[0]) {
            dispatch(ParameterAction.fetchSelectionParameter(selectionName[0])).then((params) => {
                dispatch(AlertAction.fetchPollutionSourceParams({
                    STEPId: installation.id,
                    parameters: params.map(d => d.parameterCode) || null,
                    inActivity,
                })).then(() => setDataLoaded(true))
            })
        } else {
            dispatch(AlertAction.fetchPollutionSourceParams({
                STEPId: installation.id,
                inActivity,
            })).then(() => setDataLoaded(true))
        }
    }, [])

    const data = pollutionSourceParametersFullData.map(d => ({
        parameterName: { value: d.parameterName },
        priority: { value: d.priority },
        identifier: { value: d.identifier, cellType: 'string' },
        installationName: { value: d.installationName },
        activityCode: { value: d.activityCode },
        parameterCode: { value: d.parameterCode },
        x: { value: d.x },
        y: { value: d.y },
        city: { value: d.cityName },
        insee: { value: d.city },
        category: { value: getSandreLabel(sandreCodes, SANDRE.ACTIVITES_CATEGORIE, d.activityCategory) },
        projection: { value: getSandreLabel(sandreCodes, SANDRE.PROJECTION, d.projection) },
        inActivity: { value: d.inActivity ? i18n.yes : i18n.no },
        icpe: { value: d.icpe ? i18n.yes : i18n.no },
        dischargeAgreement: { value: d.dischargeAgreement ? i18n.yes : i18n.no },
        inActivityDisplay: { value: d.inActivity && <i className='material-icons tiny table-checkbox'>check_box</i> },
        icpeDisplay: { value: d.icpe && <i className='material-icons tiny table-checkbox'>check_box</i> },
        dischargeAgreementLabel: { value: d.dischargeAgreement && <i className='material-icons tiny table-checkbox'>check_box</i> },
        activityLibelle: { value: d.activityName },
    }))

    const actions = [{
        iconName: 'file_download',
        tooltip: i18n.export,
        onClick: () => exportFile({
            data: [{ ...data[0], headers: exportHeaders }, ...data.slice(1, data.length)],
            titleFile: i18n.dangerousSituations,
        }),
        color: 'white',
    }]

    const getIntersection = () => {
        if (selectionCodes.length && parametersSelected.length) {
            return intersection(selectionCodes, parametersSelected)
        } else if (selectionCodes.length) {
            return selectionCodes
        } else if (parametersSelected.length) {
            return parametersSelected
        }
        return null
    }

    const filterActivties = () => {
        const intersect = getIntersection()
        dispatch(AlertAction.fetchPollutionSourceParams(new DtoPollutionSourceActivityParams({
            STEPId: installation.id,
            parameters: intersect,
            inActivity,
            startDate,
            endDate,
            icpe,
            dischargeAgreement,
        })))
    }

    return (
        <Stack spacing={1} sx={{ width: '100%', padding: '10px' }}>
            <Card>
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={6}>
                            <SelectionSelect
                                value={selectionName}
                                onChange={(r, v) => {
                                    setSelectionCodes(r)
                                    setSelectionName(v)
                                }}
                            />
                        </Grid>
                        <Grid size={6}>
                            <MultiAutocomplete
                                displayWithCode
                                label={i18n.parameters}
                                elements={sortBy(parameters.filter(p => p.active === '1'), 'name')}
                                clearFunction
                                values={parametersSelected}
                                onChange={setParametersSelected}
                            />
                        </Grid>
                        <Grid size={6}>
                            <SimpleDatePicker
                                value={startDate}
                                id='startDate'
                                label={i18n.startDate}
                                onChange={setStartDate}
                            />
                        </Grid>
                        <Grid size={6}>
                            <SimpleDatePicker
                                value={endDate}
                                id='endDate'
                                label={i18n.endDate}
                                onChange={setEndDate}
                            />
                        </Grid>
                        <Grid container size={6}>
                            <Grid size={4}>
                                <Checkbox
                                    label={i18n.inActivity}
                                    checked={inActivity}
                                    onChange={setInActivity}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Checkbox
                                    label={i18n.icpe}
                                    checked={icpe}
                                    onChange={setIcpe}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Checkbox
                                    label={i18n.dischargeAgreement}
                                    checked={dischargeAgreement}
                                    onChange={setDischargeAgreement}
                                />
                            </Grid>
                        </Grid>
                        <Grid container size={6} alignItems='flex-end' justifyContent='flex-end'>
                            <Button variant='contained' color='primary' onClick={filterActivties} >{i18n.search}</Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {!dataloaded ? <ProgressCard indeterminate /> : (
                <CardTable
                    title={i18n.potentialSubstanceActivity}
                    rows={data}
                    actions={actions}
                    rowsPerPageOptions={nbPerPageLabelHuge}
                    headers={[
                        { key: 'parameterName', value: i18n.parameter },
                        'priority',
                        'identifier',
                        { key: 'installationName', value: i18n.establishment },
                        'insee',
                        'city',
                        'category',
                        'activityCode',
                        'activityLibelle',
                        { key: 'inActivityDisplay', value: i18n.inActivity },
                        { key: 'icpeDisplay', value: i18n.icpe },
                        { key: 'dischargeAgreementLabel', value: i18n.agreement },
                    ]}
                    color={darkBlue}
                />
            )}
        </Stack>
    )
}

STEPPollutionSearchApp.propTypes = {
}


export default STEPPollutionSearchApp
